import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, Ruler, Factory, Truck, Cog, PackageCheck, ShieldCheck, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import ProvaSocial from "@/components/ProvaSocial";
import { Reveal, CTABand, SectionHead } from "@/components/Bits";

const FACTORY = "https://static.prod-images.emergentagent.com/jobs/d0dc872f-681a-4cd0-8351-ef2c4a72dee3/images/8258dec9a0ef71b366294a7c79791874ccfc0f83a64380cad911605f5e1d9178.jpeg";

const CAPS = [
  { icon: Ruler, t: "Projeta e Dimensiona", d: "Engenharia térmica sob medida para sua aplicação." },
  { icon: Factory, t: "Fabrica", d: "Fabricação sob encomenda, reduzindo estoque e capital parado." },
  { icon: Truck, t: "Instala", d: "Instalação e comissionamento em todo o Brasil." },
  { icon: Wrench, t: "Presta Assistência", d: "Manutenção preventiva, corretiva e retrofit." },
  { icon: Cog, t: "Fornece Peças", d: "Peças e componentes para reposição." },
  { icon: PackageCheck, t: "Pós-venda", d: "Cadastro por número de série e acompanhamento." },
];

export default function Home() {
  const { openQuote, categories } = useSite();
  const [featured, setFeatured] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/applications").then((r) => setApps(r.data.slice(0, 6)));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#27272A] bg-black">
        <img src={FACTORY} alt="Fábrica" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/20" />
        <div className="relative mx-auto max-w-[1400px] px-4 py-24 lg:px-8 lg:py-36">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="label-eyebrow mb-5 text-[#FF5500]">Fabricante de Equipamentos de Refrigeração</div>
            <h1 className="font-heading max-w-4xl text-4xl font-black leading-[1.03] text-white sm:text-5xl lg:text-7xl">
              Do projeto à fabricação: refrigeração industrial que a sua operação precisa.
            </h1>
            <p className="mt-6 max-w-2xl text-base text-[#A1A1AA] md:text-lg">
              A Global Refrigeração projeta, dimensiona e fabrica chillers, unidades condensadoras, bombas de calor,
              torres de resfriamento e soluções sob medida — com fabricação sob encomenda e assistência nacional.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button data-testid="hero-quote-button" onClick={() => openQuote()} className="rounded-none bg-[#FF5500] px-8 py-6 text-base font-bold hover:bg-[#CC4400]">
                Solicitar Orçamento
              </Button>
              <Link to="/equipamentos">
                <Button variant="outline" className="rounded-none border-[#3f3f46] bg-transparent px-8 py-6 text-base font-bold text-white hover:bg-[#1E1F22] hover:text-white">
                  Ver Equipamentos <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="relative border-t border-[#27272A] bg-[#0A0A0B]/80">
          <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-[#27272A] px-4 md:grid-cols-4 lg:px-8">
            {[["16+", "Linhas de equipamentos"], ["100%", "Sob encomenda"], ["SC → BR", "Atendimento nacional"], ["Eng.", "Dimensionamento próprio"]].map(([n, l]) => (
              <div key={l} className="px-4 py-6">
                <div className="font-heading text-2xl font-black text-white md:text-3xl">{n}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-[#A1A1AA]">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="mx-auto max-w-[1400px] px-4 py-20 lg:px-8">
        <SectionHead eyebrow="Somos fabricantes" title="Muito além da manutenção" sub="Uma operação completa: da engenharia à entrega e ao pós-venda." />
        <div className="grid grid-cols-1 gap-px border border-[#27272A] bg-[#27272A] sm:grid-cols-2 lg:grid-cols-3">
          {CAPS.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.05}>
              <div className="h-full bg-[#0A0A0B] p-8 transition-colors duration-200 hover:bg-[#151618]">
                <c.icon className="h-8 w-8 text-[#FF5500]" />
                <h3 className="font-heading mt-5 text-lg font-bold text-white">{c.t}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA]">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-t border-[#27272A] bg-[#0A0A0B]">
        <div className="mx-auto max-w-[1400px] px-4 py-20 lg:px-8">
          <SectionHead eyebrow="Portfólio" title="Linhas de equipamentos" sub="Portfólio amplo. Fabricamos sob encomenda conforme a demanda — cada produto tem seu status." />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {categories.slice(0, 8).map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.04}>
                <Link to={`/equipamentos/${c.slug}`} data-testid={`home-cat-${c.slug}`} className="group relative block aspect-square overflow-hidden border border-[#27272A]">
                  <img src={c.image} alt={c.name} className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute bottom-0 p-4">
                    <h3 className="font-heading text-base font-bold leading-tight text-white">{c.name}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/equipamentos"><Button variant="outline" className="rounded-none border-[#3f3f46] bg-transparent text-white hover:bg-[#1E1F22] hover:text-white">Ver todas as linhas <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 py-20 lg:px-8">
          <SectionHead eyebrow="Prioridade comercial" title="Destaques em produção" sub="Produtos com maior potencial de demanda: chillers, condensadoras e bombas de calor." />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </section>
      )}

      {/* APPLICATIONS */}
      <section className="border-t border-[#27272A] bg-[#0A0A0B]">
        <div className="mx-auto max-w-[1400px] px-4 py-20 lg:px-8">
          <SectionHead eyebrow="Por aplicação" title="Soluções para o seu segmento" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {apps.map((a) => (
              <Link key={a.slug} to={`/solucoes/${a.slug}`} className="border border-[#27272A] bg-[#151618] p-5 transition-colors duration-200 hover:border-[#FF5500]">
                <span className="font-heading text-sm font-bold text-white">{a.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ASSISTÊNCIA TEASER */}
      <section className="mx-auto grid max-w-[1400px] grid-cols-1 gap-8 px-4 py-20 lg:grid-cols-3 lg:px-8">
        {[[ShieldCheck, "Assistência Técnica", "Manutenção preventiva, corretiva e retrofit para prolongar a vida útil dos ativos.", "/assistencia"],
          [MapPin, "Atendimento Nacional", "Começamos forte em Santa Catarina e atendemos todo o Brasil.", "/contato"],
          [PackageCheck, "Pós-venda & Garantia", "Cadastro por número de série, histórico de manutenção e QR Code no equipamento.", "/assistencia"]].map(([Icon, t, d, to]) => (
          <Reveal key={t}>
            <Link to={to} className="block h-full border border-[#27272A] bg-[#151618] p-8 transition-colors duration-200 hover:border-[#FF5500]">
              <Icon className="h-8 w-8 text-[#007AFF]" />
              <h3 className="font-heading mt-5 text-xl font-bold text-white">{t}</h3>
              <p className="mt-2 text-sm text-[#A1A1AA]">{d}</p>
            </Link>
          </Reveal>
        ))}
      </section>

      <ProvaSocial />
      <CTABand />
    </div>
  );
}
